import { i18n } from 'next-i18next'
import {
  ErrorResponse,
  LegacyErrorResponse,
  ErrorType,
  ErrorItem,
  isSnackbarError,
  isComplexError,
  ComplexErrorDetails,
  SnackbarErrorDetails,
  BasicErrorDetails,
} from '@/types/error-response'

// Existing exports for backward compatibility
export const LOGIN_ERROR = 'Your email or password is incorrect. Please try again.'

export const is404 = ({ status }: { status: number }) => status === 404

/**
 * Extracts validation messages from insights API validation errors.
 * Supports both raw API responses and normalized ErrorResponse objects.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getInsightsValidationMessages(error: any): string[] {
  const data = error?.response?.data ?? error
  if (!data?.isValidation && data?.code !== 'VALIDATION_ERROR') {
    return []
  }

  const { details } = data
  if (!Array.isArray(details)) {
    return []
  }

  return details.flatMap((detail: any) => (
    Array.isArray(detail?.validation) ? detail.validation : []
  ))
}

export type ValidationErrorItem = {
  product: string;
  message: string;
}

/**
 * Extracts per-product validation items from an insights VALIDATION_ERROR so the
 * UI can show exactly which product/field failed. Relies on the details array
 * produced by normalizeErrorResponse (see convertDetailsToArray) — the backend
 * returns details keyed by index ("0", "1", ...) because of Thoth, which is
 * normalized to an array of `{ product, validation: string[] }`.
 * Supports both raw API responses and normalized ErrorResponse objects.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getValidationErrorItems(error: any): ValidationErrorItem[] {
  const data = error?.response?.data ?? error
  if (!data?.isValidation && data?.code !== 'VALIDATION_ERROR') {
    return []
  }

  const { details } = data
  if (!Array.isArray(details)) {
    return []
  }

  return details.flatMap((detail: any) => (
    Array.isArray(detail?.validation)
      ? detail.validation.map((message: string) => ({
        product: detail?.product ?? '',
        message,
      }))
      : []
  ))
}

/**
 * Normalizes error responses to a consistent format
 * Handles both new API format and legacy formats for backward compatibility
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeErrorResponse(error: any): ErrorResponse {
  // Handle null/undefined

  if (!error) {
    return {
      code: 'UNKNOWN_ERROR',
      message: 'An unknown error occurred',
      details: {},
    }
  }

  // Handle Insights Validation Error
  if (error?.response?.data?.code === 'VALIDATION_ERROR') {
    return {
      ...error.response.data,
      details: convertDetailsToArray(error),
    }
  }

  let validationSource = null
  if (error?.response?.data?.isValidation) {
    validationSource = error.response.data
  } else if (error?.isValidation) {
    validationSource = error
  }

  if (validationSource) {
    const validationMessages = getInsightsValidationMessages(validationSource)
    const defaultValidationMessage = i18n?.t('cases:workflow_not_compatible')
      ?? 'Chosen workflow is not compatible with this case.'

    return {
      code: 'VALIDATION_ERROR',
      message: validationMessages.length > 0
        ? validationMessages.join('\n')
        : defaultValidationMessage,
      details: validationSource.details || {},
    }
  }

  // Check if it's already in the new format
  if (error.code && error.message && error.details !== undefined) {
    return {
      code: error.code,
      message: error.message,
      details: error.details || {},
    }
  }

  // Handle axios error with response.data in new format
  if (error.response?.data?.code && error.response?.data?.message !== undefined) {
    return {
      code: error.response.data.code,
      message: error.response.data.message,
      details: error.response.data.details || {},
    }
  }

  // Handle legacy format with response.data
  const legacyData: LegacyErrorResponse = error.response?.data || error.data || error

  // Build details from legacy format
  const details: Record<string, string> = {}

  if (legacyData.title && legacyData.subtext) {
    details.title = legacyData.title
    details.subtext = legacyData.subtext
  }

  // Extract message from various possible locations
  const message = legacyData.message
    || error.response?.data?.message
    || error.message
    || 'An error occurred'

  // Extract code from various possible locations
  const code = legacyData.error
    || error.response?.data?.error
    || error.code
    || 'ERROR'

  return {
    code: String(code),
    message: String(message),
    details,
  }
}

/**
 * Converts legacy error details to an array format for backward compatibility
 * This is useful for displaying errors in a consistent way across the application
 * For example, validation errors may have multiple fields with messages
 * This function consolidates them into an array of messages
 */
export function convertDetailsToArray(error: any): BasicErrorDetails | SnackbarErrorDetails | ComplexErrorDetails {
  return Object.keys(error?.response?.data?.details || {})
    .reduce((acc, key) => {
      acc.push(error?.response?.data?.details[key])
      return acc
    }, [])
}

/**
 * Determines the type of error based on details structure
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getErrorType(details: any): ErrorType {
  if (isSnackbarError(details)) {
    return 'snackbar'
  }
  if (isComplexError(details)) {
    return 'complex'
  }
  return 'basic'
}

/**
 * Extracts the title to display for an error
 * For snackbar errors: returns details.title
 * For complex errors: returns the main message
 * For basic errors: returns the main message
 */
export function getErrorTitle(errorResponse: ErrorResponse): string {
  if (!errorResponse) {
    return ''
  }

  const { details, message } = errorResponse

  if (isSnackbarError(details)) {
    return details.title || message
  }

  return message
}

/**
 * Extracts the subtext/caption to display for an error
 * For snackbar errors: returns details.subtext
 * For other errors: returns empty string (or message for basic errors)
 */
export function getErrorSubtext(errorResponse: ErrorResponse): string {
  if (!errorResponse) {
    return ''
  }

  const { details } = errorResponse

  if (isSnackbarError(details)) {
    return details.subtext || ''
  }

  // For basic errors, we can return the message if there's no title/subtext structure
  // This maintains backward compatibility
  return ''
}

/**
 * Extracts complex error items as an array for display
 * Returns empty array if not a complex error
 */
export function getComplexErrorItems(errorResponse: ErrorResponse): ErrorItem[] {
  if (!errorResponse || !errorResponse.details) {
    return []
  }

  const { details } = errorResponse

  if (!isComplexError(details)) {
    return []
  }

  return Object.entries(details as ComplexErrorDetails).map(([key, message]) => ({
    key,
    message,
  }))
}

/**
 * Checks if an error has complex error items
 */
export function hasComplexErrorItems(errorResponse: ErrorResponse): boolean {
  return getComplexErrorItems(errorResponse).length > 0
}

/**
 * Gets a display-friendly error message for any error format
 * Useful for simple text displays without Callout component
 */
export function getErrorDisplayMessage(errorResponse: ErrorResponse): string {
  if (!errorResponse) {
    return ''
  }

  const title = getErrorTitle(errorResponse)
  const subtext = getErrorSubtext(errorResponse)
  const items = getComplexErrorItems(errorResponse)

  if (items.length > 0) {
    // For complex errors, combine title with item messages
    const itemMessages = items.map(item => item.message).join(', ')
    return `${title}: ${itemMessages}`
  }

  if (subtext) {
    return subtext
  }

  return title
}

// Codes that mean the API sent no error body: axios's own (offline, timeout, bare 4xx/5xx), or
// normalizeErrorResponse's fallbacks for an error thrown outside a request.
const NON_API_ERROR_CODES = new Set(['ERR_NETWORK', 'ERR_BAD_REQUEST', 'ERR_BAD_RESPONSE', 'ECONNABORTED', 'ETIMEDOUT', 'ERR_CANCELED', 'ERROR', 'UNKNOWN_ERROR'])
// An untranslated error code such as `APPLY_CUSTOM_DOMAIN_ALREADY_EXISTS`, not text for a person.
const ERROR_CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/

/**
 * The readable message the API sent with a failed request. `gridClient` rejects with the
 * normalized error, so this reads that shape. Anything that isn't text from the API gives ''.
 * @param error - the rejected request's error
 * @returns the API's message, or an empty string so the caller shows its localized copy
 */
export function getApiErrorMessage(error: unknown): string {
  if (!error) return ''
  const normalized = normalizeErrorResponse(error)
  if (NON_API_ERROR_CODES.has(normalized.code)) return ''
  const message = getErrorDisplayMessage(normalized)
  return ERROR_CODE_PATTERN.test(message) ? '' : message
}
